import * as React from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import GitHubIcon from '@mui/icons-material/GitHub';
import GoogleIcon from '@mui/icons-material/Google';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import { BACKEND_API_URL, GOOGLE_CLIENT_ID } from '../data/constant';
import { useEffect } from 'react';
import { useAppKit } from '@reown/appkit/react';
import { useNavigate } from 'react-router-dom';

/**
 * Sign-in panel.
 *
 * Renders a single "Sign in" trigger that opens a modal with three providers:
 * GitHub, Google, Web3 (wallet). The previous version used a vertical stack
 * of three identically-styled outlined buttons with cryptic labels
 * ("Sign In" appeared three times). Now each option has a distinct icon,
 * provider name, and a one-line subtitle explaining what it does.
 */

function logInWithGithub() {
  fetch(BACKEND_API_URL + 'oauthlogin/', {
    method: 'GET',
    credentials: 'include',
  })
    .then((response) => {
      if (!response.ok) throw new Error('OAuth init failed');
      return response.json();
    })
    .then((data) => {
      if (data.url) window.location.href = data.url;
    })
    .catch((error) => console.error('GitHub login error:', error));
}

// ---------------------------------------------------------------------------
// GIS script loader (module-level, runs once on first import of this file).
// Pre-loading here means the script is usually ready by the time the user
// opens the login modal — eliminating the "iframe hasn't rendered yet"
// window where clicks fall into empty space.
// ---------------------------------------------------------------------------
let gisScriptPromise = null;
function loadGisScript() {
  if (gisScriptPromise) return gisScriptPromise;
  gisScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      'script[src*="accounts.google.com/gsi/client"]'
    );
    if (existing) {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('GIS script load failed')),
        { once: true }
      );
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener(
      'error',
      () => reject(new Error('GIS script load failed')),
      { once: true }
    );
    document.head.appendChild(script);
  });
  return gisScriptPromise;
}

// Google sign-in flow.
//
// The visible button is a regular React button styled to match the other
// providers. The actual sign-in control is an <iframe> rendered by Google
// Identity Services into the same space. The iframe is positioned BELOW
// the custom button and the custom button has `pointer-events: none`, so
// the user sees the team's styling but the click lands on the Google
// iframe underneath, which is what actually triggers the OAuth redirect.
//
// Why not forward the click with document.querySelector + .click()?
// Because the GIS button lives inside a cross-origin iframe (accounts
// .google.com) and the parent document cannot reach into it — that's
// what made the previous version fail ~every time. The overlay pattern
// is the standard workaround Google documents for custom-styled buttons.
//
// Keyboard / a11y: the iframe is the focusable element. The visual
// button is aria-hidden + tabIndex={-1} so screen readers and the
// keyboard tab order land on the real Google button.
const GOOGLE_BUTTON_WIDTH = 300;   // GIS `width` is fixed px; sized to fit
                                   // the smallest expected modal content
                                   // area (≈256px on a 320px viewport).
const GOOGLE_BUTTON_HEIGHT = 50;   // GIS `large` size; matches the wrapper

const GoogleButton = () => {
  const signinRef = React.useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadGisScript()
      .then(() => {
        if (cancelled || !signinRef.current) return;
        if (!window.google?.accounts?.id) {
          console.error('Google Identity Services did not initialize.');
          return;
        }
        try {
          if (!GOOGLE_CLIENT_ID) {
            console.warn('Google OAuth not configured (REACT_APP_GOOGLE_CLIENT_ID not set)');
            return;
          }
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            ux_mode: 'redirect',
            login_uri: BACKEND_API_URL + 'googleoauthcallback/',
            auto_select: false,
          });
          window.google.accounts.id.renderButton(signinRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: GOOGLE_BUTTON_WIDTH,
          });
        } catch (e) {
          console.error('Google Sign-In init failed:', e);
        }
      })
      .catch((e) => console.error('Failed to load Google sign-in:', e));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        width: GOOGLE_BUTTON_WIDTH,
        maxWidth: '100%',
        height: GOOGLE_BUTTON_HEIGHT,
        // Clip the GIS iframe to the wrapper. On viewports narrower
        // than GOOGLE_BUTTON_WIDTH the iframe would otherwise overflow
        // and the user would see Google's "Sign in with Google"
        // peeking out beside the styled overlay.
        overflow: 'hidden',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {/* Real Google button — iframe. Fills the wrapper. */}
      <div
        ref={signinRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
      />
      {/* Visual overlay. Sits on top, doesn't capture clicks. */}
      <div
        aria-hidden="true"
        className="btn btn--secondary"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          justifyContent: 'flex-start',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        <GoogleIcon fontSize="small" />
        <span style={{ flex: 1, textAlign: 'left' }}>
          Continue with Google
        </span>
      </div>
    </div>
  );
};

export function VerifyWeb3Login(address, signer, navigate) {
  fetch(BACKEND_API_URL + 'nonce', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ address }),
  })
    .then((res) => {
      if (!res.ok) throw new Error('Failed to fetch nonce');
      return res.json();
    })
    .then(({ nonce }) =>
      signer.signMessage(nonce).then((signature) =>
        fetch(BACKEND_API_URL + 'verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address, signature, nonce }),
        })
          .then((res) => {
            if (!res.ok) throw new Error('Verification failed');
            return res.json();
          })
          .then((data) => {
            if (data.redirect) navigate(data.redirect);
            else alert('Login failed');
          })
      )
    )
    .catch((error) => console.error('Web3 login error:', error));
}

export default function LoginPanel({ setLoggedIn }) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const { open: openAppKit } = useAppKit();

  const handleWeb3 = () => {
    setOpen(false);
    openAppKit();
  };

  return (
    <>
      <button className="btn btn--primary btn--sm" onClick={() => setOpen(true)}>
        Sign in
      </button>

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog
          sx={{
            maxWidth: 420,
            width: '100%',
            padding: 'var(--space-8)',
            textAlign: 'center',
          }}
        >
          <h2 className="modal__title">Sign in to continue</h2>
          <p className="modal__subtitle">
            Pick a sign-in method. We'll create an account if this is your
            first time.
          </p>

          <div className="modal__actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={logInWithGithub}
              style={{ justifyContent: 'flex-start' }}
            >
              <GitHubIcon fontSize="small" />
              <span style={{ flex: 1, textAlign: 'left' }}>
                Continue with GitHub
              </span>
            </button>

            <GoogleButton />

            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleWeb3}
              style={{ justifyContent: 'flex-start' }}
            >
              <CurrencyBitcoinIcon fontSize="small" />
              <span style={{ flex: 1, textAlign: 'left' }}>
                Continue with Wallet
              </span>
            </button>
          </div>
        </ModalDialog>
      </Modal>
    </>
  );
}